import React, { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Phone,
  MessageCircle,
  Check,
  Loader,
  AlertCircle,
  Save,
  Trash2
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../context/SupabaseContext';
import { baseParent, baseStudent, baseTeacher, Parent, routes_per_type, Student, Teacher } from '../components/interfaces/All';

interface EditStudentProps {
  account_type: 'student'| 'parent' | 'teacher';
}

const EditStudent: React.FC<EditStudentProps> = ({account_type = 'student'}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const createContactTemplate = (type: 'student' | 'parent' | 'teacher'): Student | Parent | Teacher => {

    const templates = {
      student: (): Student => ( baseStudent ),
      parent: (): Parent => (baseParent),
      teacher: (): Teacher => (baseTeacher)
    };

    return templates[type as keyof typeof templates]();
  };

  const template = createContactTemplate(account_type);



  const [formData, setFormData] = useState<Student| Parent| Teacher>(template);
  const [originalData, setOriginalData] = useState<Student | Parent | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isDirty, setIsDirty] = useState(false);


  const { state, fetchStudents, updateStudentData, deleteStudent, updateParentData,  deleteParent, updateTeacherData} = useSupabase();

  // Load student data on mount / when id or students change
  useEffect(() => {
    // if students not loaded yet, fetch
    if (!state.students.length) {
      fetchStudents();
      return;
    }
    if (!id) return;

    const studentData = state.students.find(s => s.id === id);
    const base = studentData ?? template;
    setFormData(base);
    setOriginalData(base);
    setIsDirty(false);
  }, [id, state.students, fetchStudents]); // include deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setIsDirty(true);

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.first_name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    if (!validateForm()) return;

    setIsSubmitting(true);


    try {
      const commonFields = {
        id: id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
        notes: formData.notes,
      };

      switch (account_type) {
        case 'student':
          await updateStudentData( commonFields);
          break;

        case 'parent':
          await updateParentData( commonFields);
          break;

        case 'teacher':
          await updateTeacherData( commonFields);
          break;

        default:
          throw new Error(`Unknown contact type: ${account_type}`);
      }
    } catch (error) {
      console.error(`Failed to update ${account_type}:`, error);
      setErrors({ submit: `Failed to update ${account_type}. Please try again.` });
    } finally {
      setOriginalData(formData);
      setSubmitSuccess(true);
      setIsDirty(false);
      setTimeout(() => {
        navigate(routes_per_type[account_type] + `/${id}` || '/');
      }, 1200);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm(`Are you sure you want to delete this ${account_type}? This cannot be undone.`)) return;

    setIsSubmitting(true);
    try {
      account_type=='student'? deleteStudent(id) : deleteParent(id);

      console.log(`Deleting ${account_type}:`, id);
      account_type=='student' ? navigate(`/students`) : navigate(`/parents`);
      navigate(routes_per_type[account_type] || '/');
    } catch (error) {
      console.error(`Failed to delete ${account_type}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !!formData.first_name && !!formData.email && !isSubmitting;


  return (
    <div className="fade-in max-w-md mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">{`Edit ${account_type}`}</h1>
          <p className="text-text-light">
            {isDirty ? 'Unsaved changes detected' : `Update ${account_type} information`}
          </p>
        </div>
      </div>

      {submitSuccess ? (
        <div className="card p-12 text-center">
          <Check className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-emerald-600 mb-2">{`${account_type.charAt(0).toUpperCase() + account_type.slice(1)} Updated!`}</h3>
          <p className="text-text-light">Redirecting to profile...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Name */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              First Name *
            </label>
            <input
              name="name"
              type="text"
              placeholder="e.g. Emma"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                errors.name ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.first_name}
              onChange={handleChange}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Last Name
            </label>
            <input
              name="name"
              type="text"
              placeholder="e.g. Dupont"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                errors.name ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.last_name}
              onChange={handleChange}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email *
            </label>
            <input
              name="email"
              type="email"
              placeholder="emma.dupont@email.com"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
                errors.email ? 'border-red-300 bg-red-50 dark:bg-red-900/10' : 'border-border'
              }`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Phone
            </label>
            <input
              name="phone"
              type="tel"
              placeholder="+33 6 12 34 56 78"
              className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              value={formData.phone || ''}
              onChange={handleChange}
            />
          </div>

          {/* Status */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              Status
            </label>
            <select
              name="status"
              className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Notes */}
          <div className="card p-4">
            <label className="block text-sm font-semibold text-text-light mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Notes
            </label>
            <textarea
              name="notes"
              rows={4}
              placeholder="Any important information about this student..."
              className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              value={formData.notes || ''}
              onChange={handleChange}
            />
          </div>

          {/* Parent/Guardian */}
          { account_type=='student' && (
            <div className="card p-4 space-y-4">
              <label className="block text-sm font-semibold text-text-light flex items-center gap-2">
                <User className="w-4 h-4" />
                Parent/Guardian (if student is a minor)
              </label>
              <div>
                <label className="block text-xs text-text-light mb-1">Parent</label>
                {/* TODO Dropdown of all parents (multiple can be selected) */}
              </div>
            </div>
          )}

          {/* Global submit error */}
          {errors.submit && (
            <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.submit}
            </p>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <button
              type="button"
              className="btn btn-outline py-4 text-lg flex items-center justify-center gap-2"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              <Trash2 className="w-5 h-5" />
              Delete
            </button>
            <button
              type="submit"
              disabled={!canSubmit || !isDirty}
              className={`btn py-4 text-lg flex items-center justify-center gap-3 ${
                canSubmit && isDirty ? 'btn-primary' : 'btn-disabled'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isDirty ? 'Save Changes' : 'No Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditStudent;
